import { Box, Button } from '@mui/material';
import {
	startOfMonth,
	endOfMonth,
	startOfQuarter,
	endOfQuarter,
	startOfYear,
	endOfYear,
	subMonths,
	subQuarters,
} from 'date-fns';
import { LegacyRef, useCallback } from 'react';
import { registerLocale, ReactDatePickerProps, ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { CoreInputProps } from '../../core-input';
import { CalendarBase } from './calendar-base.component';
import CalendarInput, { CalendarInputProps } from './calendar-input.component';
import CalendarHeader from './calendar-header.component';
import en from 'date-fns/locale/en-US';
registerLocale('en-us', en);

export interface CoreCalendarAdvancedProps<WithRange extends boolean | undefined = undefined>
	extends Omit<ReactDatePickerProps<never, WithRange>, 'selectsRange'> {
	inputProps?: CalendarInputProps & CoreInputProps;
	showFilterIcon?: boolean;
	selectsRange?: WithRange;
	showPresets?: boolean;
	onClear?: () => void;
}

const CoreCalendarAdvanced = <WithRange extends boolean | undefined = undefined>(
	props: CoreCalendarAdvancedProps<WithRange>,
	ref: LegacyRef<HTMLInputElement> | undefined
) => {
	const {
		inputProps,
		dateFormat = 'MM/dd/yyyy',
		onClear,
		showFilterIcon,
		showPresets,
		onChange,
		...calendarProps
	} = props;
	const { i18n } = useTranslation();

	const renderMonthCustomHeader = useCallback((props: ReactDatePickerCustomHeaderProps) => {
		const { date, nextMonthButtonDisabled, prevMonthButtonDisabled, increaseMonth, decreaseMonth } = props;

		return (
			<CalendarHeader
				date={date}
				dateFormat="LLLL yyyy"
				prevUnitButtonDisabled={prevMonthButtonDisabled}
				nextUnitButtonDisabled={nextMonthButtonDisabled}
				decreaseUnit={decreaseMonth}
				increaseUnit={increaseMonth}
			/>
		);
	}, []);

	const renderYearCustomHeader = useCallback((props: ReactDatePickerCustomHeaderProps) => {
		const { date, prevYearButtonDisabled, nextYearButtonDisabled, increaseYear, decreaseYear } = props;

		return (
			<CalendarHeader
				date={date}
				dateFormat="yyyy"
				prevUnitButtonDisabled={prevYearButtonDisabled}
				nextUnitButtonDisabled={nextYearButtonDisabled}
				decreaseUnit={decreaseYear}
				increaseUnit={increaseYear}
			/>
		);
	}, []);

	const presets: Record<string, [Date, Date]> = {
		'This Month': [startOfMonth(new Date()), endOfMonth(new Date())],
		'Last Month': [startOfMonth(subMonths(new Date(), 1)), endOfMonth(subMonths(new Date(), 1))],
		'This Quarter': [startOfQuarter(new Date()), endOfQuarter(new Date())],
		'Last Quarter': [startOfQuarter(subQuarters(new Date(), 1)), endOfQuarter(subQuarters(new Date(), 1))],
		'This Year': [startOfYear(new Date()), endOfYear(new Date())],
	};

	return (
		<CalendarBase
			advanced
			selectsRange={props.selectsRange}
			startDate={props.startDate}
			endDate={props.endDate}
			onChange={onChange}
			dateFormat={dateFormat}
			showPopperArrow={false}
			locale="en-us"
			renderCustomHeader={props.showMonthYearPicker ? renderYearCustomHeader : renderMonthCustomHeader}
			timeInputLabel=""
			calendarContainer={({ className, children }) => (
				<div className={`${className} custom-calendar-with-menu`}>
					<div className="calendar-menu">
						{Object.entries(presets).map(([label, range]) => (
							<Button
								key={label}
								onClick={() =>
									onChange?.(
										range as WithRange extends false | undefined ? Date | null : [Date | null, Date | null],
										undefined
									)
								}
								variant="text"
								sx={{
									width: '100px',
									justifyContent: 'flex-start',
									textTransform: 'none',
									fontSize: 14,
									color: '#000000',
									fontWeight: 400,
									'&:hover': {
										backgroundColor: '#EBEBEB',
									},
									'&:active': {
										backgroundColor: '#EBEBEB',
									},
								}}
							>
								{label}
							</Button>
						))}
					</div>
					<div className="calendar-content">{children}</div>
				</div>
			)}
			customInput={<CalendarInput {...inputProps} ref={ref} onClear={onClear} showFilterIcon={showFilterIcon} />}
			{...calendarProps}
		/>
	);
};

export default CoreCalendarAdvanced;
